/**
 * Hook: useProfile
 * 
 * Loads user profile (access role + worker binding) when session changes
 * Manages profile loading state and error messages
 * 
 * @param {Object} session - Auth session object (from useAuthSession)
 * @returns {Object} { accessRole, boundWorkerId, profileLoading, profileMessage }
 */

import { useLayoutEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { normalizeAccessRole } from '../workforceAccessModel'

export const useProfile = (session) => {
  const [accessRole, setAccessRole] = useState('employee')
  const [boundWorkerId, setBoundWorkerId] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileMessage, setProfileMessage] = useState('')

  useLayoutEffect(() => {
    if (!session) {
      setAccessRole('employee')
      setBoundWorkerId('')
      setProfileLoading(false)
      setProfileMessage('')
      return
    }

    // as soon as we have a session, mark profile as loading synchronously
    setProfileLoading(true)
    setProfileMessage('')

    let isActive = true

    const loadProfile = async () => {
      const userId = session?.user?.id

      if (!userId) {
        if (!isActive) return
        setAccessRole('employee')
        setBoundWorkerId('')
        setProfileLoading(false)
        setProfileMessage('Access profile missing user id; defaulting to Employee access.')
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, access_role, role, worker_id')
          .eq('id', userId)
          .maybeSingle()

        if (!isActive) return

        if (error) {
          setAccessRole('employee')
          setBoundWorkerId('')
          setProfileMessage(`Unable to load profile access role: ${error.message}`)
        } else {
          setAccessRole(normalizeAccessRole(data?.access_role || data?.role || 'employee'))
          setBoundWorkerId(data?.worker_id ? String(data.worker_id) : '')
        }
      } catch (error) {
        if (!isActive) return
        setAccessRole('employee')
        setBoundWorkerId('')
        setProfileMessage(`Profile load failed: ${error?.message || 'Unknown error'}`)
      } finally {
        if (isActive) {
          setProfileLoading(false)
        }
      }
    }

    loadProfile()

    return () => {
      isActive = false
    }
  }, [session])

  return {
    accessRole,
    boundWorkerId,
    profileLoading,
    profileMessage
  }
}
